/**
 * The Procore baseline pull — the wire shape shared by `backend/` (which
 * runs the pull and stores it) and `webapp/` (which shows it).
 *
 * ## What this is
 *
 * An INTERNAL Led AI dashboard, per customer workspace, answering: for every
 * Procore user on every Procore project linked to this workspace, how many
 * Daily Log entries, Observations and Punch items did that person create (and
 * how many Punch items were they assigned), and how many photos they
 * uploaded, in each of a handful of date windows — and, per item, whether
 * the assignee / location / description was filled in. The windows are the pilot tracker's: a 4-week baseline before the
 * pilot started, then the three evaluation checkpoints (weeks 2-4, 5-7,
 * 8-10). Without a pilot start date they fall back to rolling 7 / 30 / 90
 * day windows.
 *
 * ## Nothing is pulled until a human presses the button
 *
 * Every API call made by a pull lands on a CUSTOMER's Procore account, under
 * a token one of their users granted us, and counts against their rate
 * limit. So there is no scheduler, no "refresh on page load", and no pull on
 * first visit: a pull exists only because a Led AI staff member requested one
 * from the console, and the dashboard otherwise shows the most recent stored
 * pull with its timestamp. The console reads the stored pull; it never calls
 * Procore itself.
 *
 * ## Why counts are aggregated locally, not asked of Procore
 *
 * Procore has no "count by creator" endpoint. Daily log counts accept a
 * creator filter but return one aggregate for all the creators passed; the
 * observation and task lists ignore every date filter shape this codebase
 * has tried (see `procoreSync.registry.ts`). So a pull lists each project's
 * records once — daily-log families by date window, observations, punch
 * items and photos in full — and buckets them here by `created_by.id` and
 * date.
 */

export type ProcoreBaselineEnv = "production" | "sandbox";

/** A date window, inclusive on both ends, as `YYYY-MM-DD`. */
export interface ProcoreBaselineWindow {
  /** `baseline` | `cp1` | `cp2` | `cp3` | `pilot` | `last7` | `last30` | `last90`. */
  key: string;
  label: string;
  /** What the window means, for the column tooltip. */
  hint: string;
  start: string;
  end: string;
  /** True when `end` was clipped to today — the window is still filling. */
  partial?: boolean;
  /** When `partial`, the day the window will actually end — the chart draws
   *  the band that far, though no data can lie past `end` yet. */
  until?: string;
  /** True when the window starts after today — nothing can be in it yet. */
  future?: boolean;
}

export interface ProcoreBaselineCounts {
  /** Daily-log entries across every daily-log family, bucketed by log DATE. */
  dailyLogs: number;
  /** Observations created, bucketed by `created_at` (UTC date). */
  observations: number;
  /** Punch items created, bucketed by `created_at` (UTC date). */
  punchCreated: number;
  /** Punch items on which this user appears as an assignee, bucketed by the
   *  item's `created_at`. Independent of `punchCreated`. */
  punchAssigned: number;
  /** Tasks created (the Tasks tool), bucketed by `created_at` (UTC date). */
  tasks: number;
  /** Photos uploaded, bucketed by `created_at` (UTC date). Not a "record":
   *  `authoredTotal` leaves them out, as the Team roster's own stat does. */
  photos: number;
  /**
   * How many of this user's observations / punch items / photos had the
   * field filled in — the console's "Has assignee" / "Has location" / "Has
   * description" filters count only those. `…Both` is the intersection, so
   * two filters on at once still count real items rather than a product of
   * shares. Each is ≤ its family's total.
   */
  observationsWithAssignee: number;
  observationsWithLocation: number;
  observationsWithBoth: number;
  punchWithAssignee: number;
  punchWithLocation: number;
  punchWithBoth: number;
  tasksWithAssignee: number;
  tasksWithLocation: number;
  tasksWithBoth: number;
  photosWithDescription: number;
  photosWithLocation: number;
  photosWithBoth: number;
}

/** Every counter, in one place, so folding and zeroing never miss one. */
export const PROCORE_BASELINE_COUNT_FIELDS = Object.freeze([
  "dailyLogs",
  "observations",
  "punchCreated",
  "punchAssigned",
  "tasks",
  "photos",
  "observationsWithAssignee",
  "observationsWithLocation",
  "observationsWithBoth",
  "punchWithAssignee",
  "punchWithLocation",
  "punchWithBoth",
  "tasksWithAssignee",
  "tasksWithLocation",
  "tasksWithBoth",
  "photosWithDescription",
  "photosWithLocation",
  "photosWithBoth",
] as const) satisfies readonly (keyof ProcoreBaselineCounts)[];

export const EMPTY_BASELINE_COUNTS: ProcoreBaselineCounts = Object.freeze(
  Object.fromEntries(PROCORE_BASELINE_COUNT_FIELDS.map((f) => [f, 0])) as unknown as ProcoreBaselineCounts,
);

export interface ProcoreBaselineUserRow {
  /** Procore's user id — the `id` of `created_by` / an assignment's
   *  `login_information`. The project-user directory is keyed on it too. */
  procoreUserId: number;
  name: string;
  email?: string;
  /** The user's company in Procore's project directory, when listed. */
  company?: string;
  /** The Led AI (Clerk) user this Procore user is, when an org seat's
   *  verified email matches. The pilot's own users are the ones that match. */
  ledUserId?: string;
  /** True when this user is in the project's Procore directory. A creator who
   *  has since left the project still appears, flagged false. */
  inDirectory: boolean;
  byWindow: Record<string, ProcoreBaselineCounts>;
  /**
   * The same ticks by calendar day (`YYYY-MM-DD`), for the sparklines. Only
   * days with activity are present. Optional because pulls stored before
   * this field existed have none — their rows draw no chart.
   */
  byDay?: Record<string, ProcoreBaselineCounts>;
}

export type ProcoreBaselineFamilyStatus = "ok" | "forbidden" | "truncated" | "error";

export interface ProcoreBaselineFamilyResult {
  status: ProcoreBaselineFamilyStatus;
  pages: number;
  rows: number;
  /** Rows that fell inside the span, after local date filtering. */
  rowsInSpan: number;
  error?: string;
}

export type ProcoreBaselineProjectStatus = "pending" | "running" | "done" | "failed" | "skipped";

export interface ProcoreBaselineProject {
  /** `${env}:${companyId}:${projectId}` — project ids are unique only within
   *  an environment, so the env and company are part of the identity. */
  key: string;
  env: ProcoreBaselineEnv;
  companyId: number;
  projectId: number;
  projectName: string;
  /** Every Led AI job site linked to this Procore project. */
  jobSiteIds: string[];
  jobSiteNames: string[];
  /** Whose Procore connection the calls went out under. */
  viaUserId?: string;
  viaEmail?: string;
  /** The project's time zone as Procore names it ("Pacific Time (US &
   *  Canada)"), when the read could get it. Daily-log dates live in it. */
  timezone?: string;
  status: ProcoreBaselineProjectStatus;
  error?: string;
  /** Per record family: `observations`, `punch_items`, and each daily-log
   *  family (`manpower_logs`, `notes_logs`, …). */
  families: Record<string, ProcoreBaselineFamilyResult>;
  directorySize: number;
  /**
   * Everyone in the project directory, whether or not they recorded
   * anything — the people picker offers all of them. Light: no counts (those
   * live on `users`). Absent on pulls stored before the field existed.
   */
  directory?: ProcoreBaselineDirectoryEntry[];
  /** True when the directory ran past `MAX_PAGES` or a page failed, so
   *  `directory` is a prefix of it, not all of it. */
  directoryTruncated?: boolean;
  users: ProcoreBaselineUserRow[];
  apiCalls: number;
  startedAt?: string;
  finishedAt?: string;
}

export interface ProcoreBaselineDirectoryEntry {
  procoreUserId: number;
  name: string;
  email?: string;
  company?: string;
  /** The Led AI seat with this email, when there is one. */
  ledUserId?: string;
}

export type ProcoreBaselinePullStatus = "queued" | "running" | "done" | "failed";

export interface ProcoreBaselinePull {
  id: string;
  orgId: string;
  status: ProcoreBaselinePullStatus;
  /** `setup` — the staff member's one explicit read; `weekly` — the
   *  scheduler's re-read during the pilot. Absent on reads stored before
   *  the field existed (all of them were explicit). */
  trigger?: "setup" | "weekly";
  requestedBy: { userId: string; email?: string };
  requestedAt: string;
  startedAt?: string;
  finishedAt?: string;
  /** Last time the stored document changed. A pull still `running` whose
   *  heartbeat is old was orphaned by a backend restart. */
  updatedAt: string;
  pilotStartDate?: string;
  windows: ProcoreBaselineWindow[];
  /** The union of every window — the date range the pull actually walked. */
  span: { start: string; end: string };
  projects: ProcoreBaselineProject[];
  progress: { projectsTotal: number; projectsDone: number; apiCalls: number };
  /** The last rate-limit headers Procore sent during this read: how much of
   *  the customer's hourly bucket was left when the read finished or stopped. */
  rateLimit?: { limit?: number; remaining?: number; resetAt?: string; at: string };
  /** Set while the read is sleeping through a 429 until Procore's reset;
   *  cleared when it resumes. The heartbeat keeps ticking meanwhile. */
  pausedUntil?: string;
  error?: string;
}

/** What the console's list of earlier pulls shows per entry. */
export interface ProcoreBaselinePullSummary {
  id: string;
  status: ProcoreBaselinePullStatus;
  requestedAt: string;
  finishedAt?: string;
  requestedBy: { userId: string; email?: string };
  pilotStartDate?: string;
  projectsTotal: number;
  apiCalls: number;
}

/**
 * The one-time setup a Led AI staff member does per workspace: when the pilot
 * started, and which Procore people are the pilot. Saved separately from the
 * pull so a refresh of the Procore data never loses who was chosen.
 */
export interface ProcoreBaselineCohortMember {
  procoreUserId: number;
  name: string;
  email?: string;
  company?: string;
  /** The project whose directory they were picked from — the picker groups
   *  the running list by it. Absent on cohorts saved before the field. */
  projectKey?: string;
}

/**
 * The checkpoint calendar, per team workspace. The default is the pilot
 * tracker's (4-week baseline, 1-week ramp, three 3-week checkpoints, a
 * 75-day pilot); a workspace can change the ramp, checkpoints and pilot
 * length in setup. The baseline is fixed at four weeks in the console
 * (the field stays so a stored schedule is self-describing). Lengths are in
 * DAYS so the arithmetic is exact; the console edits them in weeks.
 */
export interface ProcoreBaselineSchedule {
  /** Days before the pilot start that form the baseline. */
  baselineDays: number;
  /** Days at the start of the pilot in no checkpoint (onboarding). */
  rampDays: number;
  /** Length of each checkpoint. */
  checkpointDays: number;
  checkpointCount: number;
  /** Length of the pilot; weekly reads continue a tail past it, then stop. */
  pilotDays: number;
}

export const DEFAULT_SCHEDULE: ProcoreBaselineSchedule = Object.freeze({
  baselineDays: 28,
  rampDays: 7,
  checkpointDays: 21,
  checkpointCount: 3,
  pilotDays: 75,
});

/** The bounds a saved schedule must sit inside. Shared so the console can
 *  refuse the same values the backend would. */
export const SCHEDULE_LIMITS = Object.freeze({
  baselineDays: { min: 7, max: 56 }, // 1–8 weeks
  rampDays: { min: 0, max: 28 }, // 0–4 weeks
  checkpointDays: { min: 7, max: 56 }, // 1–8 weeks
  checkpointCount: { min: 1, max: 6 },
  pilotDays: { min: 7, max: 180 },
});

/** Null when valid; otherwise the first problem, in a sentence. */
export function scheduleProblem(s: ProcoreBaselineSchedule): string | null {
  for (const key of Object.keys(SCHEDULE_LIMITS) as Array<keyof ProcoreBaselineSchedule>) {
    const v = s[key];
    const { min, max } = SCHEDULE_LIMITS[key];
    if (!Number.isInteger(v) || v < min || v > max) return `${key} must be a whole number from ${min} to ${max}.`;
  }
  const covered = s.rampDays + s.checkpointDays * s.checkpointCount;
  if (covered > s.pilotDays) return `The ramp and ${s.checkpointCount} checkpoints need ${covered} days, more than the ${s.pilotDays}-day pilot.`;
  return null;
}

export interface ProcoreBaselineSetup {
  orgId: string;
  pilotStartDate?: string;
  /** Absent means the default. */
  schedule?: ProcoreBaselineSchedule;
  /** Empty until step 3 of the setup has been saved. */
  cohort: ProcoreBaselineCohortMember[];
  /** True once the people step has been saved, even if nobody was picked. */
  cohortChosen: boolean;
  updatedBy: { userId: string; email?: string };
  updatedAt: string;
}

export interface ProcoreBaselineResponse {
  /** The requested (or latest) pull, or null when none has ever been run. */
  pull: ProcoreBaselinePull | null;
  /** The workspace's setup, or null before the first step is saved. */
  setup: ProcoreBaselineSetup | null;
  history: ProcoreBaselinePullSummary[];
  /** The Procore-linked projects the NEXT pull would walk — shown before any
   *  pull exists, so the button's blast radius is visible before pressing it. */
  linkedProjects: Array<{
    key: string;
    env: ProcoreBaselineEnv;
    companyId: number | null;
    projectId: number;
    projectName: string;
    jobSiteNames: string[];
  }>;
}

/** The pull-request body. */
export interface ProcoreBaselinePullRequest {
  orgId: string;
  /** `YYYY-MM-DD`. Omitted, the saved setup's date is used; with neither,
   *  rolling windows. */
  pilotStartDate?: string;
}

/** The setup-save body. Fields absent are left as they were. */
export interface ProcoreBaselineSetupRequest {
  orgId: string;
  pilotStartDate?: string | null;
  cohort?: ProcoreBaselineCohortMember[];
  /** `null` restores the default. */
  schedule?: ProcoreBaselineSchedule | null;
}

/** A running pull with no heartbeat for this long is reported as failed. */
export const PROCORE_BASELINE_STALE_MS = 10 * 60_000;

/** Weekly re-reads continue for a short tail after the pilot so the last
 *  checkpoint's final week is captured, then stop for good. */
export const PROCORE_BASELINE_TAIL_DAYS = 14;
/** Re-reads are a week apart, measured from the previous read's end. */
export const PROCORE_BASELINE_WEEKLY_GAP_MS = 7 * 86_400_000;

/**
 * When the NEXT scheduled read of a set-up workspace is due, or null when
 * there will be none: no setup, no pilot date, or the pilot (plus its tail)
 * is over. Shared by the scheduler, which runs a read once this time has
 * passed, and the console, which prints it — so the two cannot disagree.
 */
export function nextWeeklyReadAt(
  setup: Pick<ProcoreBaselineSetup, "pilotStartDate" | "schedule"> | null | undefined,
  latest: Pick<ProcoreBaselinePull, "status" | "finishedAt" | "requestedAt"> | null | undefined,
  now: Date,
): Date | null {
  if (!setup?.pilotStartDate) return null;
  const end = Date.parse(`${pilotReadsEndOn(setup.pilotStartDate, setup.schedule)}T23:59:59Z`);
  if (now.getTime() > end) return null;
  if (!latest) return now;
  const last = Date.parse(latest.finishedAt ?? latest.requestedAt);
  const due = new Date(last + PROCORE_BASELINE_WEEKLY_GAP_MS);
  return due.getTime() > end ? null : due;
}

/** The last calendar day a scheduled read can happen for this pilot: the
 *  tail after the pilot's last day, which the calendar below may push past
 *  the nominal length so the last checkpoint ends on a Sunday. */
export function pilotReadsEndOn(pilotStartDate: string, schedule?: ProcoreBaselineSchedule | null): string {
  return addDays(pilotCalendar(pilotStartDate, schedule).pilotEnd, 1 + PROCORE_BASELINE_TAIL_DAYS);
}

/** The Monday on or before `ymd`. Weeks here are calendar weeks, Monday to Sunday. */
export function mondayOf(ymd: string): string {
  const dow = new Date(`${ymd}T00:00:00Z`).getUTCDay(); // 0 Sun … 6 Sat
  return addDays(ymd, -((dow + 6) % 7));
}

export interface PilotCalendar {
  /** The Monday of the week the pilot started in. */
  weekStart: string;
  /** Whole weeks before the pilot's first week. */
  baseline: { start: string; end: string };
  /** The ramp-up's last day (a Sunday), or null when CP1 starts on day one. */
  rampEnd: string | null;
  /** Each checkpoint, Monday to Sunday, back to back. */
  checkpoints: Array<{ start: string; end: string }>;
  /** The pilot's last day: its nominal length, or the last checkpoint's end if later. */
  pilotEnd: string;
}

/**
 * The pilot's calendar in whole Monday–Sunday weeks (2026-10-09). A pilot
 * starts on whatever day it starts, but every period after that is calendar
 * weeks, so a checkpoint always runs Monday to Sunday and "this week" means
 * the week on the wall. The ramp-up takes the rest of the start week, plus
 * a whole week when the start is Wednesday or later: so a Monday or Tuesday
 * start ramps for the 5 or 4 business days left in that week, and a
 * Wednesday–Friday start for those 3–1 days and the 5 of the following week
 * (8, 7 or 6). The schedule's `rampDays` is read as a number of whole weeks
 * W (7 → 1): a Monday or Tuesday start ramps W weeks counting the start
 * week, a later start W weeks after it; W = 0 is no ramp, and CP1 begins on
 * day one with the start week as its first. `checkpointDays` is likewise
 * whole weeks. The baseline is the `baselineDays`-worth of whole weeks
 * before the start week; the days of the start week before the pilot began
 * belong to nothing.
 */
export function pilotCalendar(pilotStartDate: string, schedule?: ProcoreBaselineSchedule | null): PilotCalendar {
  const sch = schedule ?? DEFAULT_SCHEDULE;
  const s = pilotStartDate;
  const weekStart = mondayOf(s);
  const dow = daysBetween(weekStart, s); // 0 Mon … 6 Sun
  const W = Math.round(sch.rampDays / 7);
  const cpWeeks = Math.max(1, Math.round(sch.checkpointDays / 7));
  // The Monday CP1 starts on: after W whole weeks for a Monday or Tuesday
  // start (Tuesday's first "week" is its 4 remaining days), after the rest of
  // the start week plus W whole weeks otherwise. With no ramp at all, CP1
  // begins on day one and its first week is the start week itself.
  const cp1Start = W === 0 ? s : addDays(weekStart, 7 * (dow <= 1 ? W - 1 : W) + 7);
  const checkpoints: Array<{ start: string; end: string }> = [];
  let start = cp1Start;
  for (let i = 0; i < sch.checkpointCount; i++) {
    const end = addDays(mondayOf(start), cpWeeks * 7 - 1); // the Sunday closing its last week
    checkpoints.push({ start, end });
    start = addDays(end, 1);
  }
  const nominalEnd = addDays(s, sch.pilotDays - 1);
  const lastCp = checkpoints[checkpoints.length - 1]?.end ?? nominalEnd;
  const baselineWeeks = Math.max(1, Math.round(sch.baselineDays / 7));
  return {
    weekStart,
    baseline: { start: addDays(weekStart, -7 * baselineWeeks), end: addDays(weekStart, -1) },
    rampEnd: W === 0 ? null : addDays(cp1Start, -1),
    checkpoints,
    pilotEnd: lastCp > nominalEnd ? lastCp : nominalEnd,
  };
}

/**
 * How the setup sheet says the ramp-up: whole weeks, and the start week's
 * remaining business days when the pilot does not start on a Monday. The
 * stepper moves only the weeks (`rampDays` = 7 × W); the days follow from
 * the start date. Monday: W weeks. Tuesday: W−1 weeks and 4 days (its first
 * week IS those 4 days). Wednesday–Friday: W weeks and 3–1 days. Saturday or
 * Sunday: W weeks. W = 0 is no ramp at all, said as "0 weeks".
 */
export function rampMakeup(pilotStartDate: string, rampDays: number): { weeks: number; days: number } {
  const W = Math.round(rampDays / 7);
  if (W === 0) return { weeks: 0, days: 0 };
  const dow = daysBetween(mondayOf(pilotStartDate), pilotStartDate);
  if (dow === 0) return { weeks: W, days: 0 };
  if (dow === 1) return { weeks: W - 1, days: 4 };
  if (dow <= 4) return { weeks: W, days: 5 - dow };
  return { weeks: W, days: 0 };
}

/**
 * Builds the date windows for a pull.
 *
 * With a pilot start date, the pilot's calendar in whole Monday–Sunday weeks
 * (`pilotCalendar`): the baseline is the four whole weeks before the start
 * week; the ramp-up — the rest of the start week, plus a whole week when the
 * start is Wednesday or later — is in no window; each checkpoint is three
 * calendar weeks, Monday to Sunday, back to back from the Monday after the
 * ramp; and `pilot` is everything from the start through the pilot's last
 * day (or today). Windows that end after today are clipped and flagged
 * `partial`; windows that start after today are kept, flagged `future`, so
 * the columns are stable across checkpoints.
 *
 * Without one, rolling 7 / 30 / 90 day windows ending today.
 */
export function buildProcoreBaselineWindows(input: {
  pilotStartDate?: string | null;
  today: string;
  schedule?: ProcoreBaselineSchedule | null;
}): { windows: ProcoreBaselineWindow[]; span: { start: string; end: string } } {
  const today = input.today;
  const sch = input.schedule ?? DEFAULT_SCHEDULE;
  const clip = (w: Omit<ProcoreBaselineWindow, "partial" | "future">): ProcoreBaselineWindow => {
    if (w.start > today) return { ...w, end: w.end, future: true };
    if (w.end > today) return { ...w, end: today, until: w.end, partial: true };
    return { ...w };
  };

  let windows: ProcoreBaselineWindow[];
  if (input.pilotStartDate) {
    const s = input.pilotStartDate;
    const cal = pilotCalendar(s, sch);
    const baselineWeeks = Math.round(daysBetween(cal.baseline.start, cal.baseline.end) + 1) / 7;
    // Week numbers count calendar weeks from the pilot's start week.
    const wk = (d: string) => Math.floor(daysBetween(cal.weekStart, d) / 7) + 1;
    const day = (d: string) => daysBetween(s, d) + 1;
    windows = [
      clip({
        key: "baseline",
        label: "Baseline",
        hint: `The ${baselineWeeks} whole week${baselineWeeks === 1 ? "" : "s"} before the week the pilot started, before Led AI.`,
        start: cal.baseline.start,
        end: cal.baseline.end,
      }),
    ];
    cal.checkpoints.forEach((cp, i) => {
      windows.push(
        clip({
          key: `cp${i + 1}`,
          label: `CP${i + 1} · wks ${wk(cp.start)}-${wk(cp.end)}`,
          hint: `Evaluation period ${i + 1}: pilot days ${day(cp.start)}-${day(cp.end)}, Monday to Sunday.`,
          start: cp.start,
          end: cp.end,
        }),
      );
    });
    windows.push(
      clip({
        key: "pilot",
        label: "Pilot to date",
        hint: `Everything since the pilot start, through day ${day(cal.pilotEnd)} or today.`,
        start: s,
        end: cal.pilotEnd,
      }),
    );
  } else {
    windows = [
      { key: "last7", label: "Last 7 days", hint: "The 7 days ending today.", start: addDays(today, -6), end: today },
      { key: "last30", label: "Last 30 days", hint: "The 30 days ending today.", start: addDays(today, -29), end: today },
      { key: "last90", label: "Last 90 days", hint: "The 90 days ending today.", start: addDays(today, -89), end: today },
    ];
  }

  const live = windows.filter((w) => !w.future);
  const start = live.reduce((min, w) => (w.start < min ? w.start : min), live[0]!.start);
  const end = live.reduce((max, w) => (w.end > max ? w.end : max), live[0]!.end);
  return { windows, span: { start, end } };
}

/** `YYYY-MM-DD` plus `days`, in UTC — the only arithmetic these windows need. */
export function addDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const t = Date.UTC(y!, (m ?? 1) - 1, d ?? 1) + days * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}

export function isYmd(value: unknown): value is string {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));
}

/** The windows a `YYYY-MM-DD` date falls in. Windows overlap (`pilot`
 *  contains the checkpoints), so a date can be in several. */
export function windowsContaining(windows: ProcoreBaselineWindow[], ymd: string): string[] {
  const keys: string[] = [];
  for (const w of windows) {
    if (w.future) continue;
    if (ymd >= w.start && ymd <= w.end) keys.push(w.key);
  }
  return keys;
}

/** Percent change from `from` to `to`, or null when there is no base. */
export function percentChange(from: number, to: number): number | null {
  if (from <= 0) return null;
  return Math.round(((to - from) / from) * 100);
}

/** Sums one user's counts across every project of a pull, per window. */
export function aggregateUsersAcrossProjects(pull: ProcoreBaselinePull): ProcoreBaselineUserRow[] {
  const byUser = new Map<number, ProcoreBaselineUserRow>();
  for (const project of pull.projects) {
    for (const user of project.users) {
      const existing = byUser.get(user.procoreUserId);
      if (!existing) {
        byUser.set(user.procoreUserId, {
          ...user,
          // `?? {}`: a zero-activity seat stored by a minimizing save has no byWindow.
          byWindow: Object.fromEntries(
            Object.entries(user.byWindow ?? {}).map(([k, v]) => [k, { ...v }]),
          ),
          ...(user.byDay
            ? { byDay: Object.fromEntries(Object.entries(user.byDay).map(([k, v]) => [k, { ...v }])) }
            : {}),
        });
        continue;
      }
      existing.inDirectory = existing.inDirectory || user.inDirectory;
      existing.email = existing.email ?? user.email;
      existing.company = existing.company ?? user.company;
      existing.ledUserId = existing.ledUserId ?? user.ledUserId;
      for (const [key, counts] of Object.entries(user.byWindow ?? {})) {
        existing.byWindow[key] = addCounts(existing.byWindow[key] ?? { ...EMPTY_BASELINE_COUNTS }, counts);
      }
      if (user.byDay) {
        existing.byDay ??= {};
        for (const [ymd, counts] of Object.entries(user.byDay)) {
          existing.byDay[ymd] = addCounts(existing.byDay[ymd] ?? { ...EMPTY_BASELINE_COUNTS }, counts);
        }
      }
    }
  }
  return [...byUser.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** Everything a user authored in a window: the pilot tracker's "logs". */
export function authoredTotal(counts: ProcoreBaselineCounts | undefined): number {
  if (!counts) return 0;
  return counts.dailyLogs + counts.observations + counts.punchCreated + (counts.tasks ?? 0);
}

/** Adds `add` into `target`. Tolerates counts stored before a field
 *  existed (an absent counter is zero), so an older read still folds. */
export function addCounts(target: ProcoreBaselineCounts, add: Partial<ProcoreBaselineCounts>): ProcoreBaselineCounts {
  for (const f of PROCORE_BASELINE_COUNT_FIELDS) target[f] = (target[f] ?? 0) + (add[f] ?? 0);
  return target;
}

/** One 7-day bin of a sparkline. */
/**
 * One bin of a chart's series. Weekly bins (`weeklySeries`) span seven days;
 * daily bins (`dailySeries`) have `start === end`. The name predates the
 * daily grain — `ProcoreBaselineBin` is the same type under its honest name.
 */
export interface ProcoreBaselineWeek {
  /** Week index relative to the pilot start (week 0 = days 1-7) — or, for a
   *  daily bin, the day index (day 0 = the start date). Rolling pulls count
   *  back from today, so the last bin is 0 or -1 and earlier are negative. */
  index: number;
  start: string;
  end: string;
  counts: ProcoreBaselineCounts;
  /** The window keys this bin lies inside, for the shaded bands. */
  windows: string[];
  /** True when the bin is still filling: a week running past today, or today itself. */
  partial: boolean;
  /** True when the bin starts after today — drawn as a band, never as a point. */
  future?: boolean;
}
export type ProcoreBaselineBin = ProcoreBaselineWeek;

/** What the bins are built over: the read's span, the workspace's windows,
 *  and — with a pilot — the day the pilot ends, so the chart always spans the
 *  whole pilot with every checkpoint on it, however much of it has happened. */
export interface ProcoreBaselineBinFrame {
  windows: ProcoreBaselineWindow[];
  span: { start: string; end: string };
  pilotStartDate?: string;
  /** The pilot's last day. Bins run to it when it lies past today. */
  horizon?: string;
}

/** The window keys a bin overlaps — by each window's real extent, so a
 *  checkpoint still filling (or not yet begun) still claims its future bins. */
function windowKeysOver(windows: ProcoreBaselineWindow[], start: string, end: string): string[] {
  return windows.filter((w) => start <= (w.until ?? w.end) && end >= w.start).map((w) => w.key);
}

/**
 * The weekly bins a pull's sparklines draw, aligned so window edges fall on
 * bin edges: with a pilot start, weeks run from the start date (so the
 * baseline is exactly bins -3..-1 and CP1 is bins 1..3); without one, weeks
 * end today. Bins cover the span and nothing more.
 */
export function weeklyBins(pull: ProcoreBaselineBinFrame): Array<Omit<ProcoreBaselineWeek, "counts">> {
  const today = pull.span.end;
  const last = pull.horizon && pull.horizon > today ? pull.horizon : today;
  // With a pilot, bins are calendar weeks (Monday to Sunday) and the pilot's
  // first week begins on its first day; without one, weeks ending today.
  const anchor = pull.pilotStartDate ? mondayOf(pull.pilotStartDate) : addDays(today, 1);
  // The first bin containing span.start.
  const offset = Math.floor(daysBetween(anchor, pull.span.start) / 7);
  const bins: Array<Omit<ProcoreBaselineWeek, "counts">> = [];
  for (let i = offset; ; i++) {
    const start = i === 0 && pull.pilotStartDate ? pull.pilotStartDate : addDays(anchor, i * 7);
    if (start > last) break;
    const end = addDays(addDays(anchor, i * 7), 6);
    const future = start > today;
    bins.push({ index: i, start, end, windows: windowKeysOver(pull.windows, start, end), partial: end > today, ...(future ? { future } : {}) });
  }
  return bins;
}

/** `b - a` in whole days (UTC). */
export function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

/** A user's (or a group's) per-day counts folded into the pull's weekly bins. */
export function weeklySeries(
  pull: ProcoreBaselineBinFrame,
  byDay: Record<string, ProcoreBaselineCounts> | undefined,
): ProcoreBaselineWeek[] {
  const bins = weeklyBins(pull).map((b) => ({ ...b, counts: { ...EMPTY_BASELINE_COUNTS } }));
  if (!byDay) return bins;
  for (const [ymd, counts] of Object.entries(byDay)) {
    const bin = bins.find((b) => ymd >= b.start && ymd <= b.end);
    if (bin) addCounts(bin.counts, counts);
  }
  return bins;
}

/** How a chart bins the per-day ticks: one point per day, or per week. */
export type ProcoreBaselineGrain = "day" | "week";

/**
 * The daily bins: one per calendar day of the span, every day whether or not
 * anything was recorded, so a quiet day draws as a dip and not a gap. Each
 * `index` counts days from the same anchor `weeklyBins` uses, so a day with
 * `index % 7 === 0` starts a pilot week and `Math.floor(index / 7)` is the
 * week bin it belongs to. Today is `partial` — still filling.
 */
export function dailyBins(pull: ProcoreBaselineBinFrame): Array<Omit<ProcoreBaselineWeek, "counts">> {
  const today = pull.span.end;
  const last = pull.horizon && pull.horizon > today ? pull.horizon : today;
  const anchor = pull.pilotStartDate ?? addDays(today, 1);
  const bins: Array<Omit<ProcoreBaselineWeek, "counts">> = [];
  for (let d = pull.span.start; d <= last; d = addDays(d, 1)) {
    const future = d > today;
    bins.push({
      index: daysBetween(anchor, d),
      start: d,
      end: d,
      windows: windowKeysOver(pull.windows, d, d),
      partial: d >= today,
      ...(future ? { future } : {}),
    });
  }
  return bins;
}

/** A user's (or a group's) per-day counts, one bin per day of the span. */
export function dailySeries(
  pull: ProcoreBaselineBinFrame,
  byDay: Record<string, ProcoreBaselineCounts> | undefined,
): ProcoreBaselineWeek[] {
  const bins = dailyBins(pull).map((b) => ({ ...b, counts: { ...EMPTY_BASELINE_COUNTS } }));
  if (!byDay) return bins;
  const byStart = new Map(bins.map((b) => [b.start, b]));
  for (const [ymd, counts] of Object.entries(byDay)) {
    const bin = byStart.get(ymd);
    if (bin) addCounts(bin.counts, counts);
  }
  return bins;
}

/** The series a chart draws at the chosen grain. */
export function seriesAt(
  grain: ProcoreBaselineGrain,
  pull: ProcoreBaselineBinFrame,
  byDay: Record<string, ProcoreBaselineCounts> | undefined,
): ProcoreBaselineWeek[] {
  return grain === "day" ? dailySeries(pull, byDay) : weeklySeries(pull, byDay);
}

/**
 * A window's pace as a weekly rate, pro-rated over the days of it that have
 * fully happened: the window's start through yesterday (or its own end, if
 * earlier), so a checkpoint reads from its second day and a figure never
 * dips at noon because today is half over. Null before the window has a
 * complete day. For a 28-day baseline or a 21-day checkpoint it is exactly
 * the weekly average.
 */
export function weeklyRate(
  byDay: Record<string, ProcoreBaselineCounts> | undefined,
  w: ProcoreBaselineWindow | undefined,
  today: string,
  value: (counts: ProcoreBaselineCounts) => number,
): number | null {
  if (!byDay || !w || w.future) return null;
  const yesterday = addDays(today, -1);
  const through = w.end < yesterday ? w.end : yesterday;
  if (through < w.start) return null;
  const days = daysBetween(w.start, through) + 1;
  let total = 0;
  for (const [ymd, c] of Object.entries(byDay)) if (ymd >= w.start && ymd <= through) total += value(c);
  return (total / days) * 7;
}

/** The ramp-up: the pilot's first days before CP1, derived from the pilot
 *  and CP1 windows so a stored pull needs no new key. Undefined without a
 *  pilot or when the schedule has no ramp (CP1 starts on day one). */
export function rampWindow(windows: ProcoreBaselineWindow[], today: string): ProcoreBaselineWindow | undefined {
  const pilot = windows.find((w) => w.key === "pilot");
  const cp1 = windows.find((w) => w.key === "cp1");
  if (!pilot || !cp1 || cp1.start <= pilot.start) return undefined;
  const end = addDays(cp1.start, -1);
  const days = daysBetween(pilot.start, end) + 1;
  const base = { key: "ramp", label: "Ramp up", hint: `The first ${days === 7 ? "week" : `${days} days`} of the pilot, before CP1.`, start: pilot.start, end };
  if (base.start > today) return { ...base, future: true };
  if (base.end > today) return { ...base, end: today, until: base.end, partial: true };
  return base;
}

// ---------------------------------------------------------------------------
// The people picker's directory, in pages
// ---------------------------------------------------------------------------

/** One row of the picker: a directory member, or someone the read found who
 *  has since left the project, with their records across the pull. */
export interface ProcoreDirectoryRow {
  procoreUserId: number;
  name: string;
  email?: string;
  company?: string;
  ledUserId?: string;
  /** Authored records in the read, but no longer in the project directory. */
  left?: boolean;
  /** Daily logs + observations + punch created + punch assigned, across the pull. */
  records: number;
}

/** One page of a project's directory — `GET /api/procore-baseline/directory`. */
export interface ProcoreDirectoryPage {
  project: string;
  projectName: string;
  rows: ProcoreDirectoryRow[];
  /** The offset of the next page, or null on the last one. */
  nextCursor: string | null;
  /** Rows matching `q` (every row when there is no `q`). */
  total: number;
  /** Every row, query or not. */
  all: number;
  /** The read stopped before the end of Procore's directory. */
  truncated: boolean;
}

export const DIRECTORY_PAGE_DEFAULT = 50;
export const DIRECTORY_PAGE_MAX = 200;

/**
 * A project's picker rows: its directory — or, on a pull stored before
 * directories were kept, the people the read found in it — plus anyone the
 * read found who has since left, each with their records across the whole
 * pull. Seats first, then by records, then name. Pure, so the console's test
 * stand-in serves the same pages the backend does.
 */
export function directoryRows(pull: Pick<ProcoreBaselinePull, "projects">, projectKey: string): ProcoreDirectoryRow[] | null {
  const project = pull.projects.find((p) => p.key === projectKey);
  if (!project) return null;
  const records = new Map<number, number>();
  for (const u of aggregateUsersAcrossProjects(pull as ProcoreBaselinePull)) {
    records.set(u.procoreUserId, Object.values(u.byWindow ?? {}).reduce((s, c) => s + authoredTotal(c) + (c.punchAssigned ?? 0), 0));
  }
  const rows = new Map<number, ProcoreDirectoryRow>();
  const dir = project.directory ?? project.users.filter((u) => u.inDirectory).map((u) => ({ procoreUserId: u.procoreUserId, name: u.name, email: u.email, company: u.company, ledUserId: u.ledUserId }));
  for (const d of dir) {
    rows.set(d.procoreUserId, {
      procoreUserId: d.procoreUserId,
      name: d.name,
      ...(d.email ? { email: d.email } : {}),
      ...(d.company ? { company: d.company } : {}),
      ...(d.ledUserId ? { ledUserId: d.ledUserId } : {}),
      records: records.get(d.procoreUserId) ?? 0,
    });
  }
  for (const u of project.users) {
    const known = rows.get(u.procoreUserId);
    if (known) {
      // The read's row knows things a directory entry may not: the seat it
      // matched, a company or email the directory left blank.
      if (u.ledUserId && !known.ledUserId) known.ledUserId = u.ledUserId;
      if (u.email && !known.email) known.email = u.email;
      if (u.company && !known.company) known.company = u.company;
      continue;
    }
    rows.set(u.procoreUserId, {
      procoreUserId: u.procoreUserId,
      name: u.name,
      ...(u.email ? { email: u.email } : {}),
      ...(u.company ? { company: u.company } : {}),
      ...(u.ledUserId ? { ledUserId: u.ledUserId } : {}),
      left: true,
      records: records.get(u.procoreUserId) ?? 0,
    });
  }
  return [...rows.values()].sort(compareDirectoryRows);
}

function compareDirectoryRows(a: ProcoreDirectoryRow, b: ProcoreDirectoryRow): number {
  return (a.ledUserId ? 0 : 1) - (b.ledUserId ? 0 : 1) || b.records - a.records || a.name.localeCompare(b.name);
}

/** 0 = a field starts with the query, 1 = a field contains it, 2 = no match. */
function directoryRank(row: ProcoreDirectoryRow, q: string): number {
  const fields = [row.name, row.email ?? "", row.company ?? ""].map((f) => f.toLowerCase());
  if (fields.some((f) => f.startsWith(q))) return 0;
  if (fields.some((f) => f.includes(q))) return 1;
  return 2;
}

/**
 * One page of `rows`: filtered by `q` (a case-insensitive substring of name,
 * email or company; a prefix match ranks first), from the offset `cursor`,
 * `limit` long. The same offset cursor `searchProjects` uses for projects.
 */
export function pageDirectory(
  rows: ProcoreDirectoryRow[],
  options: { q?: string | null; cursor?: string | null; limit?: number | null },
): Pick<ProcoreDirectoryPage, "rows" | "nextCursor" | "total" | "all"> {
  const q = options.q?.trim().toLowerCase() ?? "";
  const limit = Math.min(Math.max(Math.floor(options.limit ?? DIRECTORY_PAGE_DEFAULT) || DIRECTORY_PAGE_DEFAULT, 1), DIRECTORY_PAGE_MAX);
  const parsed = Number(options.cursor ?? "0");
  const offset = Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
  const matched = q
    ? rows
        .map((row) => ({ row, rank: directoryRank(row, q) }))
        .filter((x) => x.rank < 2)
        .sort((a, b) => a.rank - b.rank || compareDirectoryRows(a.row, b.row))
        .map((x) => x.row)
    : rows;
  const page = matched.slice(offset, offset + limit);
  const end = offset + page.length;
  return { rows: page, nextCursor: end < matched.length ? String(end) : null, total: matched.length, all: rows.length };
}

/** The pull without its directories — what the dashboard GET answers. The
 *  sizes and the truncation flag stay; the names come a page at a time. */
export function stripDirectories<T extends Pick<ProcoreBaselinePull, "projects">>(pull: T): T {
  return {
    ...pull,
    projects: pull.projects.map((p) => {
      if (!("directory" in p)) return p;
      const { directory: _directory, ...rest } = p;
      return rest as typeof p;
    }),
  };
}

/** Per-day counts summed across users. */
export function sumByDay(users: Array<Pick<ProcoreBaselineUserRow, "byDay">>): Record<string, ProcoreBaselineCounts> {
  const out: Record<string, ProcoreBaselineCounts> = {};
  for (const u of users) {
    for (const [ymd, counts] of Object.entries(u.byDay ?? {})) {
      out[ymd] = addCounts(out[ymd] ?? { ...EMPTY_BASELINE_COUNTS }, counts);
    }
  }
  return out;
}
